import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Plus, X } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

export interface Vendedor {
  id: string; // Ahora obligatorio
  name: string;
  phone: string;
  account?: string;
  ranges: { from: string; to: string }[];
}

interface VendedorModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (vendedor: Vendedor) => void;
  vendedor?: Vendedor | null;
}

export function VendedorModal({ open, onClose, onSave, vendedor }: VendedorModalProps) {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    account: '',
    ranges: [{ from: '', to: '' }],
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (vendedor) {
      setForm({
        name: vendedor.name || '',
        phone: vendedor.phone || '',
        account: vendedor.account || '',
        ranges: vendedor.ranges && vendedor.ranges.length > 0 ? vendedor.ranges : [{ from: '', to: '' }],
      });
    } else {
      setForm({ name: '', phone: '', account: '', ranges: [{ from: '', to: '' }] });
    }
  }, [vendedor, open]);

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleRangeChange = (idx: number, field: 'from' | 'to', value: string) => {
    setForm((prev) => ({
      ...prev,
      ranges: prev.ranges.map((r, i) => (i === idx ? { ...r, [field]: value } : r)),
    }));
  };

  const addRange = () => {
    setForm((prev) => ({ ...prev, ranges: [...prev.ranges, { from: '', to: '' }] }));
  };

  const removeRange = (idx: number) => {
    setForm((prev) => ({ ...prev, ranges: prev.ranges.filter((_, i) => i !== idx) }));
  };

  const resetForm = () => {
    setForm({ name: '', phone: '', account: '', ranges: [{ from: '', to: '' }] });
    setError(null);
  };

  const validate = () => {
    if (!form.name.trim()) return 'El nombre es obligatorio.';
    if (!form.phone.trim()) return 'El teléfono es obligatorio.';
    if (!/^\d{8,}$/.test(form.phone.trim())) return 'El teléfono debe ser válido (mínimo 8 dígitos).';
    if (form.ranges.length === 0) return 'Agrega al menos un rango de lotes.';
    for (const r of form.ranges) {
      if (!r.from.trim() || !r.to.trim()) return 'Completa todos los rangos.';
      if (isNaN(Number(r.from)) || isNaN(Number(r.to))) return 'Los rangos deben ser números.';
      if (Number(r.from) > Number(r.to)) return 'El rango "Desde" debe ser menor o igual a "Hasta".';
    }
    return null;
  };

  const handleSave = async () => {
    setError(null);
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setSaving(true);
    const vendedorId = vendedor?.id || uuidv4();
    onSave({
      id: vendedorId,
      name: form.name.trim(),
      phone: form.phone.trim(),
      account: form.account.trim() || '',
      ranges: form.ranges.map(r => ({ from: r.from.trim(), to: r.to.trim() })),
    });
    setSaving(false);
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v: boolean) => { if (!v) { resetForm(); onClose(); } }}>
      <DialogContent className="max-w-lg bg-background border border-border rounded-xl p-8">
        <DialogHeader>
          <DialogTitle className="text-foreground">Agregar Nuevo Vendedor</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Completa la información del vendedor y asigna los rangos de lotes correspondientes.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
          <div className="space-y-2">
            <Label className="text-foreground">Nombre del Vendedor <span className="text-red-500">*</span></Label>
            <Input value={form.name} onChange={e => handleChange('name', e.target.value)} placeholder="Nombre completo" className="bg-secondary border-input focus:border-ring text-foreground placeholder:text-muted-foreground" />
            {vendedor?.id && (
              <div className="text-xs text-muted-foreground mt-1">ID: <span className="font-mono bg-muted px-2 py-0.5 rounded text-foreground">{vendedor.id}</span></div>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-foreground">Teléfono <span className="text-red-500">*</span></Label>
            <Input value={form.phone} onChange={e => handleChange('phone', e.target.value)} placeholder="Ej. 50212345678" className="bg-secondary border-input focus:border-ring text-foreground placeholder:text-muted-foreground" />
          </div>
        </div>
        <div className="mb-2">
          <Label className="mb-1 block text-foreground">Número de Cuenta Bancaria</Label>
          <Input value={form.account} onChange={e => handleChange('account', e.target.value)} placeholder="Número de cuenta" className="bg-secondary border-input focus:border-ring text-foreground placeholder:text-muted-foreground" />
        </div>
        <div className="border-t border-border pt-4 mt-2">
          <div className="flex items-center justify-between mb-2">
            <div>
              <Label className="text-foreground">Rangos de Lotes Asignados</Label>
              <p className="text-xs text-muted-foreground">Define los rangos de lotes que manejará este vendedor</p>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addRange} className="gap-1 border-input hover:bg-accent text-foreground">
              <Plus className="h-4 w-4" /> Agregar Rango
            </Button>
          </div>
          <div className="space-y-2">
            {form.ranges.map((r, idx) => (
              <div key={idx} className="grid grid-cols-2 gap-2 items-center relative bg-card border border-border rounded-lg p-2">
                <Input value={r.from} onChange={e => handleRangeChange(idx, 'from', e.target.value)} placeholder="Ej. 1" className="bg-secondary border-input focus:border-ring text-foreground placeholder:text-muted-foreground" />
                <Input value={r.to} onChange={e => handleRangeChange(idx, 'to', e.target.value)} placeholder="Ej. 18" className="bg-secondary border-input focus:border-ring text-foreground placeholder:text-muted-foreground" />
                {form.ranges.length > 1 && (
                  <button type="button" onClick={() => removeRange(idx)} className="absolute -right-8 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-destructive">
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
        {error && <div className="text-destructive text-sm mb-2">{error}</div>}
        <div className="flex gap-2 mt-4">
          <Button onClick={handleSave} className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90" disabled={saving}>
            <Plus className="h-4 w-4 mr-2" /> Crear Vendedor
          </Button>
          <Button type="button" variant="outline" onClick={() => { resetForm(); onClose(); }} className="border-input hover:bg-accent text-foreground">
            Cancelar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}